import { Component, OnInit, Renderer2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ToastrService } from 'ngx-toastr';

import { AuthService } from 'app/security/auth.service';
import { PolicyService } from 'app/services/policy-service';
import { EventService } from 'app/services/event-service';
import { DasLexService } from 'app/services/daslex-service';
import { ChatService } from 'app/services/chat-service';
import { ClaimService } from 'app/services/claim-service';
import { CommonsText } from 'app/commons/CommonsText';
import { CommonsUtils } from 'app/commons/CommonsUtils';
import { environment } from 'environments/environment';

import { QuickAction } from '../../components/main/quick-actions/quick-actions.component';
import { ExpedienteResumen } from '../../components/main/cases/cases.component';
import { ConsultaLegal } from '../../components/main/consultas/consultas.component';
import { ProductoActivo } from '../../components/main/active-products/active-products.component';
import { OtroSeguro } from '../../components/main/other-insurance/other-insurance.component';

import { CategoriaDataDTO } from 'app/shared/models/wordpress/CategoriaDataDTO';
import { BlogJuridicoService } from 'app/shared/services/wordpress/blog-juridico.service';

@Component({
    selector: 'app-main-page',
    templateUrl: './main-page.component.html',
    styleUrls: ['./main-page.component.scss']
})
export class MainPageComponent implements OnInit {

    userName: string = '';

    private readonly allQuickActions: QuickAction[] = [
        { iconKey: 'chat',   title: 'Chatea con un abogado',    desc: 'Resuelve tus dudas legales con especialistas', link: '/#' },
        { iconKey: 'add',    title: 'Abrir expediente',         desc: 'Indica lo ocurrido y completa tus datos',      link: '/events/register' },
        { iconKey: 'click',  title: 'Consulta tus expedientes', desc: 'Sigue su estado y evolución',               link: '/expedientes' },
        { iconKey: 'shield', title: 'Consulta tus seguros',     desc: 'Revisa tus pólizas y coberturas',              link: '/insurances' }
    ];

    // El chat con abogado solo se ofrece a quien tiene una DJ29 en vigor (hasLegalPolicies).
    // Se identifica por iconKey, igual que hace quick-actions al abrir el modal: por título
    // dependería del copy, y un cambio de texto colaría la acción a quien no le corresponde.
    get quickActions(): QuickAction[] {
        return this.hasLegalPolicies
            ? this.allQuickActions
            : this.allQuickActions.filter(action => action.iconKey !== 'chat');
    }

    // El catálogo se resuelve una sola vez: otherProducs() crea objetos nuevos en cada llamada y
    // desde un getter haría que *ngFor volviese a pintar las tarjetas en cada ciclo de detección.
    private readonly allOtherInsurances: OtroSeguro[] = CommonsUtils.otherProducs();

    /**
     * No se ofrece el Abogado de Familia a quien ya tiene contratada una póliza de Asistencia
     * Jurídica Familiar (DJ29 en vigor, hasLegalPolicies). Se filtra por id y no por título,
     * por el mismo motivo que quickActions usa iconKey.
     */
    get otherInsurances(): OtroSeguro[] {
        return this.hasLegalPolicies
            ? this.allOtherInsurances.filter(seguro => seguro.id !== CommonsUtils.FAMILY_LAWYER_ID)
            : this.allOtherInsurances;
    }

    expedientes: ExpedienteResumen[] = [];
    consultasLegales: ConsultaLegal[] = [];
    productosActivos: ProductoActivo[] = [];

    loadingExpedientes: boolean = false;
    loadingConsultas: boolean = false;
    loadingProductos: boolean = false;

    showCategoria: boolean = false;
    categorias: CategoriaDataDTO[];
    lstCategoriaDestacados: CategoriaDataDTO[];
    showDirectAreas: boolean = true;
    hasLegalPolicies: boolean = false;

    // "Mis beneficios" solo para tomadores de PP09/PP11/DJ13/DJ29 (ver PolicyService).
    showBenefits: boolean = false;

    constructor(private policyService: PolicyService,
                private authService: AuthService,
                private eventService: EventService,
                private dasLexService: DasLexService,
                private blogJuridicoService: BlogJuridicoService,
                private chatService: ChatService,
                private claimService: ClaimService,
                private toastr: ToastrService,
                private renderer: Renderer2,
                @Inject(DOCUMENT) private document: Document) {
    }

    ngOnInit() {
        const user = this.authService.getUser();
        if (user && user.name) {
            this.userName = user.name.toString();
        }

        this.showDirectAreas = this.authService.getSelectedEntity() === 'ONLYGAL';

        if (this.categorias === undefined) {
            // Único punto de escritura en localStorage: persistimos las categorías
            // cargadas en memoria (desde WordPress al arrancar la app) una vez la carga
            // ha terminado. El resto de pantallas las leen de localStorage sin volver a
            // consultar WordPress. showCategoria habilita <app-articles> DESPUÉS de la
            // escritura para que su ngOnInit lea ya el localStorage relleno.
            this.blogJuridicoService.guardarCategoriasEnStorage().then(() => {
                this.categorias = this.blogJuridicoService.getCategoriasCompletas();
                this.showCategoria = true;
            });
        }

        this.loadCurrentPolicies();
        // Los expedientes no son exclusivos del area directa: en reaseguro tambien los hay
        // (el menu lateral los ofrece como "Partes") y la seccion se pinta siempre.
        this.loadExpedientes();

        if (this.showDirectAreas) {
            this.loadLegalPolicies();
            this.policyService.hasBenefitProducts()
                .then(has => { this.showBenefits = has; });
        }
    }

    private loadLegalPolicies(): void {
        this.policyService.hasLegalAssistanceProduct()
            .then(has => {
                this.hasLegalPolicies = has;
                if (this.hasLegalPolicies) {
                    this.chatService.ensureWidgetLoaded();
                    this.loadConsultasLegales();
                }
            });
    }

    private loadExpedientes(): void {
        this.loadingExpedientes = true;
        this.eventService.getOpenEvents()
            .then(success => {
                const list = (success && success.response) ? success.response : [];

               this.expedientes = list.map(e => ({
                    titulo: e.versionType || '',
                    estado: e.eventStatus || '',
                    polizaNumero: e.policyNumber || e.idRea || '',
                    expedienteNumero: (e.eventNumber || '').toString(),
                    eventYear: (e.eventYear || '').toString(),
                    fechaOcurrencia: this.formatDay(e.eventEndDate),
                    esAseguradoEnExpediente: e.isInsuredInEvent === true
                } as ExpedienteResumen));

                this.loadingExpedientes = false;
            })
            .catch(() => {
                this.expedientes = [];
                this.loadingExpedientes = false;
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_PARTES, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                    timeOut: 5000
                });
            });
    }

    /**
     * Carga (máximo 3, mediante paginación) las consultas de siniestros del usuario logado
     * desde el nuevo servicio de consultas (Innova).
     */
    private loadConsultasLegales(): void {
        this.loadingConsultas = true;
        this.claimService.list(0, 3)
            .then(page => {
                const list = (page && page.content) ? page.content : [];
                this.consultasLegales = list.map(c => ({
                    titulo: [c.queryTypeDescription, c.querySubtypeDescription].filter(Boolean).join(': '),
                    detalle: c.description || '',
                    fechaApertura: c.date || '',
                    tramitador: c.handlerName || '',
                    estado: ''
                } as ConsultaLegal));
                this.loadingConsultas = false;
            })
            .catch(() => {
                this.consultasLegales = [];
                this.loadingConsultas = false;
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_CONSULTAS, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                    timeOut: 5000
                });
            });
    }

    /**
     *  - Calcula y persiste la relación del cliente (TOMA/ASEG).
     *  - Si es área directa (ONLYGAL), construye la lista de productos activos.
     */
    private loadCurrentPolicies(): void {
        if (this.showDirectAreas) {
            this.loadingProductos = true;
        }
        this.policyService.getCurrent()
            .then(success => {
                const list = (success && success.response) ? success.response : [];

                if (this.showDirectAreas) {
                    this.productosActivos = list.map(p => ({
                        nombre: p.product || '',
                        poliza: p.number || '',
                        estado: this.normaliseEstado(p.status),
                        vigencia: this.composeVigencia(p.startDate, p.endDate),
                        branchType: p.branchType || ''
                    } as ProductoActivo));
                    this.loadingProductos = false;
                }
            })
            .catch(() => {
                this.productosActivos = [];
                this.loadingProductos = false;
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_POLIZAS, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                    timeOut: 5000
                });
            });
    }

    /*
    private composeLetrado(letrado: any): string {
        if (!letrado) {
            return '';
        }
        const nombre = letrado.nombre || '';
        const apellidos = letrado.apellidos || '';
        return (nombre + ' ' + apellidos).trim();
    }
    */

    /** Normaliza el estado de la póliza: sin valor => "Activo"; en otro caso, capitalizado. */
    private normaliseEstado(raw: any): string {
        if (!raw) { return 'Activo'; }
        const str = raw.toString();
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    private composeVigencia(startDate: any, endDate: any): string {
        const start = this.formatDay(startDate);
        const end = this.formatDay(endDate);
        if (!start && !end) {
            return '';
        }
        return 'Del ' + start + ' a ' + end;
    }

    private formatDay(value: any): string {
        if (!value) {
            return '';
        }
        const str = value.toString();
        const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            return isoMatch[3] + '/' + isoMatch[2] + '/' + isoMatch[1];
        }
        const date = new Date(str);
        if (isNaN(date.getTime())) {
            return str;
        }
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        return pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear();
    }
}
